import { Children, cloneElement, isValidElement, ReactElement, ReactNode } from 'react'

// eslint-disable-next-line no-restricted-imports
import { PortableText, type PortableTextBlock, type PortableTextComponents } from 'next-sanity'

import { createPortableTextLists } from '@/app/components/portableText/lists'
import { createPortableTextMarks } from '@/app/components/portableText/marks'
import { TrackingLocation } from '@/app/utils/analytics'

const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g

function convertEmailsToMailtoLinks(text: string): ReactNode[] {
  const parts: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  // Reset regex state
  EMAIL_REGEX.lastIndex = 0

  while ((match = EMAIL_REGEX.exec(text)) !== null) {
    // Add text before the email
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }

    // Add email as mailto link
    const email = match[0]
    parts.push(
      <a key={`email-${match.index}`} href={`mailto:${email}`} className="underline">
        {email}
      </a>,
    )

    lastIndex = match.index + email.length
  }

  // Add remaining text after last email
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts.length > 0 ? parts : [text]
}

function processChildren(children: ReactNode, enableMailtoLinks: boolean): ReactNode {
  if (!enableMailtoLinks) return children

  return Children.map(children, (child) => {
    // If child is a string, convert emails to mailto links
    if (typeof child === 'string') {
      const processed = convertEmailsToMailtoLinks(child)
      return processed.length === 1 ? processed[0] : <>{processed}</>
    }

    // If child is a valid React element, recursively process its children
    if (isValidElement(child)) {
      const element = child as ReactElement<{ children?: ReactNode }>
      if (element.props.children) {
        return cloneElement(element, {
          children: processChildren(element.props.children, enableMailtoLinks),
        })
      }
    }

    return child
  })
}

export default function PortableTextMinimal({
  value,
  tracking,
  enableMailtoLinks = false,
}: {
  value: PortableTextBlock[]
  tracking: TrackingLocation
  enableMailtoLinks?: boolean
}) {
  const components: PortableTextComponents = {
    block: {
      normal: ({ children }) => <>{processChildren(children, enableMailtoLinks)}</>,
    },
    marks: createPortableTextMarks(tracking),
    ...createPortableTextLists(),
  }

  return <PortableText components={components} value={value} />
}
